import React, { useId } from 'react';
import styled from 'styled-components';
import { colorVars } from '@routific/hive/v5';
import { loaderMotion } from './loaderKeyframes';

/**
 * index.html carries a static copy of this mark at rest, to cover the gap before the
 * bundle mounts. Geometry, colours and the resting poses are duplicated there, so changes
 * to the petal path, the viewBox or the overlap pairs need the same edit in both
 * places or the two will disagree at the handoff. The boot loader cannot read
 * Hive's hashed `colorVars.brand.routificLogo` names (the theme has not loaded yet), so
 * it paints the same light values through local `--brand-logo-*` variables.
 */

/**
 * The petals sweep 31.3 -> 328.7 of the playground's 360-unit box, so the viewBox below
 * is cropped to just that span. The assembled mark is ~55% of the cropped box, making
 * this ~1.8x the logo you actually see: 266 here reads as a ~147px mark.
 *
 * The swept extent is measured across the whole cycle, not just at the keyframe stops —
 * a petal rotates as it travels, so mid-travel it sweeps wider than at either end.
 */
const DEFAULT_MARK_SIZE = 266;

// Cropped from "0 0 360 360". Keeping the origin at 0 matters: the generated transforms
// are absolute user-space coordinates resolved against `transform-origin: 0 0`, so a
// non-zero min-x/min-y would shift every petal. The offset lives on a wrapper group
// instead, which composes on top and leaves those coordinates untouched.
const VIEWBOX_SIZE = 298;
const VIEWBOX_OFFSET = -31;

const Svg = styled.svg`
  display: block;
  overflow: visible;

  ${loaderMotion}
`;

// One petal of the mark, lifted verbatim from public/img/routific_logo.svg. The four
// petals there are this same shape at 0/90/180/270, so rotating it reproduces the logo
// rather than approximating it.
const PETAL_PATH = 'M24.7934 27.9755C25.2095 26.4226 26.4225 25.2096 27.9754 24.7935'
  + 'L71.1318 13.2298C72.6847 12.8137 74.3417 13.2577 75.4785 14.3945'
  + 'L107.071 45.9872C108.208 47.124 108.652 48.781 108.236 50.3339'
  + 'L96.6721 93.4903C96.256 95.0432 95.043 96.2562 93.4901 96.6723'
  + 'L50.3337 108.236C48.7808 108.652 47.1239 108.208 45.9871 107.071'
  + 'L14.3944 75.4786C13.2575 74.3418 12.8136 72.6849 13.2297 71.132'
  + 'L24.7934 27.9755Z';

// Each pair is drawn as one petal clipped by another, so the wedge is derived per frame.
// A blend mode can't stand in for this: the brand overlap is a fixed colour, and a blend
// result depends on both inputs.
const OVERLAP_PAIRS = [[0, 1], [1, 2], [2, 3], [3, 0], [0, 2], [1, 3]];

interface Props {
  size?: number;
  className?: string;
  /**
   * Negative offset into the loop, from the shared clock in loaderTimeline.ts. Passed as a
   * custom property rather than interpolated into the styled component so a new value
   * doesn't mint a new class on every mount.
   */
  delayMs?: number;
}

const LoaderMark: React.FC<Props> = ({ size = DEFAULT_MARK_SIZE, className, delayMs }) => {
  // `href="#petal"` and `clip-path: url(#c0)` resolve against the document, not the
  // nearest <svg>. Without unique ids, a second instance would be clipped by the first
  // instance's petals, and lose all four the moment that instance unmounted.
  const uid = useId().replace(/:/g, '');
  const petalId = `${uid}-petal`;
  const clipId = (i: number) => `${uid}-c${i}`;

  return (
    <Svg
      className={className}
      style={delayMs === undefined
        ? undefined
        : ({ '--loader-mark-delay': `${delayMs}ms` } as React.CSSProperties)}
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <path id={petalId} d={PETAL_PATH} />
        {[0, 1, 2, 3].map((i) => (
          <clipPath key={i} id={clipId(i)}>
            <use href={`#${petalId}`} className={`k k${i}`} />
          </clipPath>
        ))}
      </defs>

      <g transform={`translate(${VIEWBOX_OFFSET},${VIEWBOX_OFFSET})`}>
        <use href={`#${petalId}`} className="k k0" fill={colorVars.brand.routificLogo.hexagons1} />
        <use href={`#${petalId}`} className="k k2" fill={colorVars.brand.routificLogo.hexagons1} />
        <use href={`#${petalId}`} className="k k3" fill={colorVars.brand.routificLogo.hexagons2} />
        <use href={`#${petalId}`} className="k k1" fill={colorVars.brand.routificLogo.hexagons2} />

        <g opacity="0.75" fill={colorVars.brand.routificLogo.hexagonOverlap}>
          {OVERLAP_PAIRS.map(([petal, clip]) => (
            <g key={`${petal}-${clip}`} clipPath={`url(#${clipId(clip)})`}>
              <use href={`#${petalId}`} className={`k k${petal}`} />
            </g>
          ))}
        </g>
      </g>
    </Svg>
  );
};

export default LoaderMark;
